<script setup lang="ts">
type TCookieConsent = 'required' | 'all' | 'true';

const COOKIE_CONSENT_KEY = 'cookie_accepted';
const COOKIE_POLICY_LINK = 'https://cre.ru/cookie-policy';
const COOKIE_MAX_AGE = 31536000; // Год
const FADE_OUT_DURATION = 200;

const cookieConsent = useCookie<TCookieConsent | null>(COOKIE_CONSENT_KEY, {
  path: '/',
  sameSite: 'lax',
  maxAge: COOKIE_MAX_AGE,
});

const isClosing = ref(false);
const isVisible = ref(!cookieConsent.value);

const acceptCookies = (value: Exclude<TCookieConsent, 'true'>) => {
  if (!isVisible.value || isClosing.value) {
    return;
  }

  cookieConsent.value = value;
  isClosing.value = true;

  window.setTimeout(() => {
    isVisible.value = false;
  }, FADE_OUT_DURATION);
};
</script>

<template>
  <div
    v-if="isVisible"
    :class="[$style.cookie, { [$style.cookie_closing!]: isClosing }]"
  >
    <div :class="$style.cookie__container">
      <div :class="$style.cookie__row">
        <div :class="$style.cookie__text">
          <p>
            Наш сайт использует файлы cookie для улучшения пользовательского
            опыта, сбора статистики и показа персонализированных рекомендаций.
          </p>
          <p>
            Нажав «Принять все», вы даете согласие на обработку файлов cookie в
            соответствии с
            <NuxtLink
              :to="COOKIE_POLICY_LINK"
              target="_blank"
              rel="noopener noreferrer"
            >
              Политикой обработки файлов cookie</NuxtLink
            >.
          </p>
        </div>
        <div :class="$style.cookie__buttons">
          <Button
            theme="red"
            size="small"
            :class="$style.cookie__button"
            @click="acceptCookies('required')"
          >
            Только обязательные
          </Button>
          <Button
            theme="red"
            size="small"
            :class="$style.cookie__button"
            @click="acceptCookies('all')"
          >
            Принять все
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>

<style module lang="scss">
.cookie {
  position: fixed;
  left: var(--space-3x);
  right: var(--space-3x);
  bottom: var(--space-3x);
  z-index: 5;
  opacity: 1;
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;

  &_closing {
    opacity: 0;
    transform: translateY(var(--space-1x));
    pointer-events: none;
  }

  &__container {
    max-width: 1600px;
    margin: 0 auto;
    padding: var(--space-4x);
    color: var(--color-white);
    background: #2b2d32;
    border-radius: 10px;
    box-shadow: 0 0 4px rgba(255, 255, 255, 0.2);
  }

  &__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-3x);
  }

  &__text {
    display: flex;
    flex-direction: column;
    @include text_caption1;

    p {
      margin: 0;
    }

    a {
      color: inherit;
      text-decoration: underline;
      text-underline-offset: 2px;

      &:hover {
        text-decoration: none;
      }
    }
  }

  &__buttons {
    display: flex;
    align-items: center;
    gap: var(--space-2-5x);
    flex-shrink: 0;
  }

  &__button {
    width: auto !important;
    white-space: nowrap;
    text-transform: uppercase;
  }
}

@media (max-width: 1023px) {
  .cookie {
    &__row {
      flex-direction: column;
      align-items: stretch;
    }

    &__buttons {
      justify-content: flex-start;
      flex-wrap: wrap;
    }
  }
}

@media (max-width: 479px) {
  .cookie {
    left: var(--space-2x);
    right: var(--space-2x);
    bottom: var(--space-2x);

    &__container {
      padding: var(--space-2x);
    }

    &__buttons {
      flex-direction: column-reverse;
      align-items: stretch;
      gap: var(--space-1x);
    }

    &__button {
      width: 100% !important;
    }
  }
}
</style>
